import { useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  canAccessRoute,
  canSeeReportesInNav,
  CAPABILITIES,
  ROLES,
} from '../config/permissions'

/**
 * Hook que expone permisos alineados con el backend.
 * Usar para ocultar/mostrar botones, enlaces y rutas según el rol.
 */
export function usePermissions() {
  const { user } = useAuth()
  const rawRole = (
    user?.tipoUsuario ||
    user?.tipo_usuario ||
    user?.role ||
    user?.rol ||
    ''
  ).trim()
  const normalizedRole = rawRole
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

  let role = rawRole
  if (normalizedRole === 'DUENO' || normalizedRole === 'DUEÑO') {
    role = ROLES.DUEÑO
  } else if (normalizedRole === 'PET_SITTER' || normalizedRole === 'PETSITTER') {
    role = ROLES.PET_SITTER
  } else if (normalizedRole === 'VETERINARIO') {
    role = ROLES.VETERINARIO
  } else if (normalizedRole === 'ADMIN') {
    role = ROLES.ADMIN
  } else if (normalizedRole === 'SUPERADMIN') {
    role = ROLES.SUPERADMIN
  }
  const veterinarioData = user?.veterinarios?.[0]
  const isVeterinarioAdmin = role === ROLES.VETERINARIO &&
    (veterinarioData?.es_admin_clinica === true || veterinarioData?.esAdminClinica === true)
  const userClinicaId = veterinarioData?.id_clinica ?? veterinarioData?.idClinica ?? null
  const tieneClinica = role === ROLES.VETERINARIO && !!userClinicaId && veterinarioData?.activo !== false

  return useMemo(() => ({
    role,
    user,

    isDueno: role === ROLES.DUEÑO,
    isPetSitter: role === ROLES.PET_SITTER,
    isVeterinario: role === ROLES.VETERINARIO,
    isAdmin: role === ROLES.ADMIN || role === ROLES.SUPERADMIN,
    isSuperAdmin: role === ROLES.SUPERADMIN,

    isVeterinarioAdmin,
    userClinicaId: userClinicaId != null ? Number(userClinicaId) : undefined,
    tieneClinica,

    canAccessRoute: (path) => canAccessRoute(path, role),
    canSeeReportesInNav: canSeeReportesInNav(role),

    canAccessReportes: CAPABILITIES.canAccessReportes(role),
    canAccessUsuarios: CAPABILITIES.canAccessUsuarios(role),
    canAccessConfiguracion: CAPABILITIES.canAccessConfiguracion(role),
    canCreateFicha: CAPABILITIES.canCreateFicha(role),
    canAccessSuscripcionesClinicas: CAPABILITIES.canAccessSuscripcionesClinicas(role),
    canAccessClinicas: CAPABILITIES.canAccessClinicas(role),
    canAccessVeterinarios: CAPABILITIES.canAccessVeterinarios(role),
    canAccessSuscripcionesPendientes: CAPABILITIES.canAccessSuscripcionesPendientes(role, isVeterinarioAdmin),

    requiresSuperAdmin: CAPABILITIES.requiresSuperAdmin(role),
    requiresAdmin: CAPABILITIES.requiresAdmin(role),

    canEditClinica: CAPABILITIES.canEditClinica(role, isVeterinarioAdmin),
    canCreateClinica: CAPABILITIES.canCreateClinica(role, isVeterinarioAdmin, tieneClinica),
    canCreateVeterinario: CAPABILITIES.canCreateVeterinario(role, isVeterinarioAdmin),
    canManageVeterinario: CAPABILITIES.canManageVeterinario(role, isVeterinarioAdmin),

    canCreateMascota: CAPABILITIES.canCreateMascota(role),
    canAprobarCita: CAPABILITIES.canAprobarCita(role),
    canAsignarFechaCita: CAPABILITIES.canAsignarFechaCita(role),
    canCompletarCita: CAPABILITIES.canCompletarCita(role),
    canRechazarCita: CAPABILITIES.canRechazarCita(role),
  }), [user, role, isVeterinarioAdmin, userClinicaId, tieneClinica])
}
